/* eslint-disable @typescript-eslint/no-explicit-any */

import React, { useState } from "react";
import Icon from "../../components/Icon";
import cn from "classnames";
import CreateOrderModal from "../../components/Modals/CreateOrderModal/CreateOrderModal";
import LogoutModalButton from "../../components/LogoutModalButton/LogoutModalButton";
import { useNavigateToDeviSubscribe } from "../../hooks/useNavigateToDeviSubscribe";
import { ProfilePayload } from "../../models/userModels";
import PostItem from "../PostsPage/Post";
import { TranslationObject } from "../../models/translationModal";
import { DeviCategory } from "../../models/deviModel";
import { getTranslation } from "../../untils/index";

interface ArticleItemProps {
    category: DeviCategory;
    entities?: TranslationObject;
    isSpecialist?: boolean;
    level?: number;
    parentKey?: string;
    hasSubscription?: boolean;
    paymentUrl?: string;
    onCustomerRequestAccess?: () => void;
    profile?: ProfilePayload | null;
    onCustomerBuyClick?: () => void;
}

const ArticleItem: React.FC<ArticleItemProps> = ({
    category,
    entities,
    isSpecialist,
    hasSubscription,
    paymentUrl,
    onCustomerRequestAccess,
    profile,
    onCustomerBuyClick,
    level = 0,
    parentKey = "",
}) => {
    const t = (entities: any, code: string) => getTranslation(entities, code);
    const navigateToDeviSubscribe = useNavigateToDeviSubscribe({
        profile,
        onCustomerBuyClick,
    });

    const {
        id: categoryId,
        name = "",
        items = [],
        count = 0,
        open: defaultOpen = false,
    } = category;

    const [isOpen, setIsOpen] = useState(false);

    const hasChildren = Array.isArray(items) && items.length > 0;
    const isPostLevel = level === 1;

    const stableCategoryKey = categoryId
        ? `cat_${categoryId}`
        : `${parentKey}_${level}`.replace(/\s+/g, "_");

    const scrollToTariffs = () => {
        const possibleSelectors = [".tariff-table"];

        const tryScroll = () => {
            for (const selector of possibleSelectors) {
                const element = document.querySelector(selector);
                if (element) {
                    element.scrollIntoView({
                        behavior: "smooth",
                        block: "start",
                    });
                    return true;
                }
            }
            return false;
        };

        const found = tryScroll();

        if (!found) {
            let attempts = 0;
            const maxAttempts = 10;
            const interval = setInterval(() => {
                attempts++;
                const foundNow = tryScroll();

                if (foundNow || attempts >= maxAttempts) {
                    clearInterval(interval);
                }
            }, 200);
        }
    };

    const handleViewPricesClick = () => {
        scrollToTariffs();
    };

    if (count === 0 && !hasChildren) {
        return null;
    }

    const shouldBlurPost = (postIndex: number) => {
        return !hasSubscription && postIndex >= 1;
    };

    const postsToDisplay = items;

    const highlightedCategory = level === 0 && defaultOpen;

    const shouldShowFavorite = defaultOpen === true;

    return (
        <div
            className={cn(
                {
                    "accordion__element accordion__element-main": level === 0,
                    "accordion__element accordion__element-main-open":
                        level === 0 && isOpen,
                    "accordion__element accordion__content-parent": level > 0,
                    "highlighted-padding": highlightedCategory,
                },
                {
                    "is-active": isOpen,
                    "has-posts": isPostLevel && hasChildren,
                    "has-children": !isPostLevel && hasChildren,
                }
            )}
        >
            {hasChildren || isPostLevel ? (
                <div
                    className={cn({
                        "accordion__button-wrapper": highlightedCategory,
                        "accordion__button-wrapper--deep": level > 0,
                    })}
                >
                    <button
                        className={cn({
                            "accordion__element-button": level === 0,
                            "accordion__element-parent-button": level > 0,
                        })}
                        type="button"
                        onClick={() => setIsOpen(!isOpen)}
                        disabled={isPostLevel && !hasChildren}
                    >
                        <Icon
                            type={isOpen ? "arrow-down" : "arrow-right"}
                            className={cn(
                                "accordion__element-button-icon",
                                isOpen
                                    ? "accordion__element-button-icon--open"
                                    : "accordion__element-button-icon--closed"
                            )}
                            width={
                                isOpen
                                    ? level > 0
                                        ? 14
                                        : 20
                                    : level > 0
                                    ? 9
                                    : 33
                            }
                            height={
                                isOpen
                                    ? level > 0
                                        ? 9
                                        : 33
                                    : level > 0
                                    ? 14
                                    : 20
                            }
                        />

                        <div
                            className={cn(
                                {
                                    "accordion__element-button-title":
                                        level === 0,
                                    "accordion__element-parent-button-title":
                                        level === 1,
                                    "accordion__element-post-button-title":
                                        level === 2,
                                },
                                {
                                    "is-open": isOpen,
                                }
                            )}
                        >
                            <span
                                className={cn("accordion__element-count", {
                                    "accordion__element-button-count":
                                        level === 0,
                                    "accordion__element-parent-button-count":
                                        level === 1,
                                    "accordion__element-post-button-count":
                                        level === 2,
                                })}
                            >
                                ({count > 0 ? count : 0})
                            </span>
                            {name}
                        </div>
                    </button>
                    {level > 0 && isOpen && shouldShowFavorite && (
                        <div className="article-item__favorite-mark">
                            <Icon
                                type="favorite"
                                width={32}
                                height={32}
                                className="favorite-mark__icon"
                            />
                        </div>
                    )}
                    {level > 0 && !isOpen && shouldShowFavorite && (
                        <div className="article-item__favorite-mark">
                            <Icon
                                type="favorite"
                                width={32}
                                height={32}
                                className="favorite-mark__icon"
                            />
                        </div>
                    )}
                    {highlightedCategory && (
                        <div className="favorite-mark">
                            <Icon
                                type="favorite"
                                width={32}
                                height={32}
                                className="favorite-mark__icon"
                            />
                            <p className="favorite-mark__text">
                                {entities?.devi_posts_my_category?.value ??
                                    "Моя категория"}
                            </p>
                        </div>
                    )}
                </div>
            ) : isSpecialist ? (
                <LogoutModalButton
                    isSpecialist={isSpecialist}
                    entities={entities}
                    classes={["accordion__element-parent-button"]}
                >
                    <>
                        <span className="accordion__element-parent-button-title">
                            {name}
                        </span>
                        <span className="accordion__element-parent-button-count">
                            ({count > 0 ? count : 0})
                        </span>
                    </>
                </LogoutModalButton>
            ) : (
                <CreateOrderModal
                    entities={entities}
                    category={category}
                    type={category}
                    classes="accordion__element-parent-button"
                >
                    <span className="accordion__element-parent-button-title">
                        {name}
                    </span>
                    <span className="accordion__element-parent-button-count">
                        ({count > 0 ? count : 0})
                    </span>
                </CreateOrderModal>
            )}

            {isOpen && isPostLevel && hasChildren && (
                <div className="posts-container">
                    <div className="posts-grid">
                        {postsToDisplay.map((post: any, index: number) => {
                            const postKey = post?.id
                                ? `post_${post.id}`
                                : `post_${stableCategoryKey}_${index}`;

                            return (
                                <PostItem
                                    className={
                                        shouldBlurPost(index) ? "post-blur" : ""
                                    }
                                    isClickable={hasSubscription}
                                    paymentUrl={
                                        !hasSubscription
                                            ? paymentUrl
                                            : undefined
                                    }
                                    onSubscribeClick={
                                        !hasSubscription
                                            ? navigateToDeviSubscribe
                                            : undefined
                                    }
                                    onCustomerRequestAccess={
                                        onCustomerRequestAccess
                                    }
                                    entities={entities}
                                    disable={!hasSubscription}
                                    key={postKey}
                                    post={post}
                                />
                            );
                        })}

                        {!hasSubscription && (
                            <div className="subscription-wrapper">
                                <button
                                    type="button"
                                    className="subscription-button"
                                    onClick={navigateToDeviSubscribe}
                                >
                                    {t(entities, "devi_price_list_btn_text")}
                                </button>
                                <button
                                    type="button"
                                    className="view-prices-button"
                                    onClick={handleViewPricesClick}
                                >
                                    {t(entities, "devi_view_prices_btn_text")}
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {isOpen && !isPostLevel && hasChildren && (
                <div className="accordion__wrapper accordion__wrapper-bg">
                    <div className="accordion accordion-posts">
                        {items.map((subCategory: any, index: number) => {
                            const highlightedSubCategory = subCategory?.open;
                            const subCategoryKey = subCategory?.id
                                ? `subcat_${subCategory.id}`
                                : `subcat_${stableCategoryKey}_${index}`;

                            return (
                                <div
                                    key={subCategoryKey}
                                    className={cn({
                                        "accordion__subcategory-wrapper":
                                            highlightedSubCategory,
                                    })}
                                >
                                    <ArticleItem
                                        category={subCategory}
                                        entities={entities}
                                        isSpecialist={isSpecialist}
                                        hasSubscription={hasSubscription}
                                        paymentUrl={paymentUrl}
                                        onCustomerRequestAccess={
                                            onCustomerRequestAccess
                                        }
                                        profile={profile}
                                        onCustomerBuyClick={onCustomerBuyClick}
                                        level={level + 1}
                                        parentKey={stableCategoryKey}
                                    />
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ArticleItem;
